import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import type { DecisionDetail } from '@hector/shared-types';
import { cn } from '@/lib/utils';

/**
 * Bouton « Télécharger » d'une décision en PDF — action de la CHROME (panneau /
 * page), distincte du document `DecisionView`.
 *
 * L'état `downloading` couvre le CHARGEMENT DYNAMIQUE du module de rendu, pas le
 * rendu lui-même : celui-ci est synchrone depuis que le logo est un data URI
 * embarqué dans `@hector/decision-document` (2026-09-10).
 */
export function DownloadDecisionButton({
  decision,
  className,
}: {
  decision: DecisionDetail;
  className?: string;
}) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      // decisionPdfExport tire jsPDF (+ moteur prose) ; chargé en dynamique au
      // clic pour le sortir du chunk de la chrome dossier. (#345)
      const { exportDecisionPdf } = await import('@/components/case/jurisprudence/decisionPdfExport');
      await exportDecisionPdf(decision);
    } catch (err) {
      // Échec de génération PDF (ex. logo injoignable + jsPDF en erreur) : non bloquant,
      // l'utilisateur peut réessayer. On trace pour le diagnostic.
      console.error('[DownloadDecisionButton] échec export PDF décision', { id: decision.id, err });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={downloading}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border border-grey-200 px-2.5 py-1 text-xs font-medium text-charcoal transition-colors hover:bg-grey-50 disabled:cursor-default disabled:opacity-60',
        className,
      )}
    >
      {downloading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Download className="h-3.5 w-3.5" />
      )}
      <span>Télécharger</span>
    </button>
  );
}
